import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
  
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import ApiService from '../services/api';


const PrivacyPoliciesPage: React.FC = () => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const { 
    sessionId, 
    token, 
    fetchPrivacyPolicyTranslations, 
    // getPrivacyPolicyTranslation,
    privacyPolicyTranslationData, 
    getTranslation,
    currentLanguage
  } = useAppContext();

  // Function to resize iframe based on its actual content height
  const resizeIframe = (iframe: HTMLIFrameElement) => {
    try {
      const doc = iframe.contentWindow?.document;
      if (!doc) return;

      // Use body scrollHeight for a more accurate content height
      const body = doc.body;
      const html = doc.documentElement;
      const contentHeight = Math.max(
        body ? body.scrollHeight : 0,
        html ? html.scrollHeight : 0,
      );

      if (contentHeight && !Number.isNaN(contentHeight)) {
        // Set iframe height exactly to its content height (auto based on content)
        iframe.style.height = `${contentHeight}px`;
      }
    } catch (error) {
      console.error('Error resizing iframe:', error);
    }
  };

  // Store the current view when component mounts if no previous view is stored
  useEffect(() => {
    const storedPrevView = sessionStorage.getItem('privacyPolicyPrevView');
    const selectedPolicy = sessionStorage.getItem('selectedPolicy');
    
    if (!storedPrevView) {
      // If no previous view is stored, default to home
      sessionStorage.setItem('privacyPolicyPrevView', 'home');
    }

    // Auto-expand the selected policy accordion once if valid
    const validPolicyIds = new Set([
      'distributor-privacy-terms',
      'orbo-privacy',
      'herbalife-privacy',
      'data-privacy-terms',
    ]);
    if (selectedPolicy && validPolicyIds.has(selectedPolicy)) {
      setExpandedAccordion(selectedPolicy);
    }
    sessionStorage.removeItem('selectedPolicy');
  }, []);

  // Fetch privacy policy translations when component mounts
  useEffect(() => {
    const loadPrivacyPolicyTranslations = async () => {
      try {
        const apiService = new ApiService(sessionId, token);
        // Use language from global context
        const selectedLanguage = currentLanguage || 'en';
        await fetchPrivacyPolicyTranslations(apiService, selectedLanguage);
      } catch (error) {
        console.error('Failed to load privacy policy translations:', error);
      }
    };

    loadPrivacyPolicyTranslations();
  }, [sessionId, token, fetchPrivacyPolicyTranslations]);


  // Don't clean up stored previous view - let it persist for navigation
  // The view will be cleaned up when the user actually navigates away via the back button


  const handlePrivacyWebFormClick = () => {
    window.open('https://privacyportal.onetrust.com/webform/46601746-6006-4c21-88ec-15dfa5b7dcdc/349bb89d-7008-46f7-b765-717966e11235', '_blank', 'noopener,noreferrer');
  };

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Helper function to create full HTML document for iframe
  const createFullHtmlContent = (htmlContent: string | undefined, fallbackContent: string) => {
    let content = htmlContent || fallbackContent;
    
    // Remove max-width, padding, font-family, and text-decoration from inline styles in the content
    if (content) {
      content = content
        .replace(/max-width\s*:\s*[^;]+;?/gi, '')
        .replace(/width\s*:\s*[^;]+;?/gi, '')
        .replace(/height\s*:\s*[^;]+;?/gi, '')
        .replace(/padding\s*:\s*[^;]+;?/gi, '')
        .replace(/padding-top\s*:\s*[^;]+;?/gi, '')
        .replace(/padding-bottom\s*:\s*[^;]+;?/gi, '')
        .replace(/padding-left\s*:\s*[^;]+;?/gi, '')
        .replace(/padding-right\s*:\s*[^;]+;?/gi, '')
        .replace(/margin\s*:\s*[^;]+;?/gi, '')
        .replace(/margin-top\s*:\s*[^;]+;?/gi, '')
        .replace(/margin-bottom\s*:\s*[^;]+;?/gi, '')
        .replace(/margin-left\s*:\s*[^;]+;?/gi, '')
        .replace(/margin-right\s*:\s*[^;]+;?/gi, '')
        .replace(/font-family\s*:\s*[^;]+;?/gi, '')
        .replace(/font-size\s*:\s*[^;]+;?/gi, '')
        .replace(/color\s*:\s*[^;]+;?/gi, '')
        .replace(/text-decoration\s*:\s*[^;]+;?/gi, '')
        .replace(/line-height\s*:\s*[^;]+;?/gi, '')
        .replace(/style\s*=\s*["'][^"']*max-width[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*width[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*height[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*padding[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*padding-top[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*padding-bottom[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*padding-left[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*padding-right[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*margin[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*margin-top[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*margin-bottom[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*margin-left[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*margin-right[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*font-family[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*font-size[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*color[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*text-decoration[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*line-height[^"']*["']/gi, '')
        // Remove empty HTML elements (run multiple times to catch nested empty elements)
        .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
        .replace(/<(\w+)[^>]*>\s*&nbsp;\s*<\/\1>/gi, '')
        .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
        .replace(/<(\w+)[^>]*>\s*&nbsp;\s*<\/\1>/gi, '')
        .replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            @font-face {
              font-family: 'HerbalifeFont';
              src: url('/fonts/HerbalifeNatural-Light.otf') format('opentype');
              font-weight: 300;
            }
            @font-face {
              font-family: 'HerbalifeFont';
              src: url('/fonts/HerbalifeNatural-Regular.otf') format('opentype');
              font-weight: 400;
            }
            @font-face {
              font-family: 'HerbalifeFont';
              src: url('/fonts/HerbalifeNatural-Bold.otf') format('opentype');
              font-weight: 700;
            }
            * {
              box-sizing: border-box;
              padding-top: 0;
              padding-bottom: 0;
              margin-top: 0;
              margin-bottom: 0;
            }
            body {
              font-family: 'HerbalifeFont', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: var(--color-text-primary);
              margin: 0;
              padding: 0;
              background: var(--color-background);
              font-size: 14px;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
              text-rendering: optimizeLegibility;
            }
            h1, h2, h3, h4, h5, h6 {
              color: var(--color-text-primary);
              margin-top: 0;
              margin-bottom: 1rem;
              font-weight: 600;
            }
            h1 { font-size: 1.5rem; }
            h2 { font-size: 1.3rem; }
            h3 { font-size: 1.2rem; }
            h4 { font-size: 1.1rem; }
            h5 { font-size: 1rem; }
            h6 { font-size: 0.9rem; }
            p {
              margin-bottom: 1rem;
              line-height: 1.6;
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
              text-align: justify;
            }
              :before, :after {
              padding-right: 5px
              }
            strong, b {
              font-weight: 600;
            }
            em, i {
              font-style: italic;
            }
            ul, ol {
              margin-bottom: 1rem;
              padding-left: 1.5rem;
              font-size: 14px;
              font-weight: 700 !important;
            }
            li {
              margin-bottom: 0.5rem;
              line-height: 1.5;
              font-size: 14px;
              font-weight: 400;
              word-wrap: break-word;
              word-break: break-word;
              text-align: justify !important;
            }
              span {
                    word-wrap: break-word;
              word-break: break-word;
              text-align: justify;
              }
            br {
              line-height: 1.6;
            }
            a {
            font-size: 14px;
              color: var(--color-text-primary);
              text-decoration: underline;
            }
            a:hover {
              color: #004499;
            }
            blockquote {
              margin: 1rem 0;
              padding-left: 1rem;
              border-left: 3px solid var(--color-border-light);
              font-style: italic;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 1rem;
            }
            th, td {
              padding: 8px 12px;
              text-align: left;
              border-bottom: 1px solid var(--color-border-light);
            }
            th {
              background-color: #f5f5f5;
              font-weight: 600;
            }
            .highlight {
              background-color: #fff3cd;
              padding: 1rem;
              border: 1px solid #ffeaa7;
              border-radius: 4px;
              margin: 1rem 0;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
  };

  const privacyPolicies = [
    {
      id: 'distributor-privacy-terms',
      title: getTranslation('Txt855'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.distributor_privacy_policy?.content_html,
        '<p>Distributor Privacy Terms content is loading...</p>'
      )
    },
    {
      id: 'orbo-privacy',
      title:  getTranslation('Txt853'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.orbo_privacy_policy?.content_html,
        '<p>Orbo Privacy Policy content is loading...</p>'
      )
    },
    {
      id: 'herbalife-privacy',
      title: getTranslation('Txt871'),
      type: 'content',
      content: (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'var(--color-text-primary)' }}>
           <a style={{
            color: 'var(--color-text-primary)',
            textDecoration: 'underline',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
           }} href="https://www.herbalife.com/en-us/footer/privacy-policy-by-country" target="_blank" rel="noopener noreferrer"> https://www.herbalife.com/en-us/footer/privacy-policy-by-country</a>  
          </Typography>
        </Box>
      )
    },

    {
      id: 'data-privacy-terms',
      title: getTranslation('Txt867'),
      type: 'iframe',
      content: createFullHtmlContent(
        privacyPolicyTranslationData?.privacy_policy?.data_privacy_terms?.content_html,
        '<p>Distributor Privacy Policy content is loading...</p>'
      )
    }
  ];

  return (
    <Layout showBackButton={true}>
      <Box
        sx={{
          // minHeight: '100vh',
          backgroundColor: 'var(--color-background)',
          width: '100%',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 }
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              mb: { xs: 1, sm: 2 },
              width: '100%',
              maxWidth: '100%',
              color: 'var(--color-text-primary)',
              fontSize: { xs: '1.2rem', sm: '2.5rem', md: '3.5rem' },
              lineHeight: { xs: 1.2, sm: 1.1, md: 1 },
              textAlign: 'center',
              wordBreak: 'break-word',
            }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {getTranslation('Txt849')}
            </Box>
            {' '}
            <Box component="span" sx={{ fontWeight: 400 }}>
              {getTranslation('Txt850')}
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'var(--color-text-primary)',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            {getTranslation('Txt851')}
          </Typography>
        </Box>

        {/* Privacy Policy Accordions */}
        <Box sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}>
          {privacyPolicies.map((policy, index) => (
            <React.Fragment key={policy.id}>
              <Accordion
                expanded={expandedAccordion === policy.id}
                onChange={handleAccordionChange(policy.id)}
                sx={{
                  boxShadow: 'none',
                  border: 'none',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: 0,
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                  backgroundColor:
                    expandedAccordion === policy.id
                      ? 'var(--color-background)'
                      : '#ffffff',
                    borderBottom: '1px solid var(--color-border)',
                    minHeight: '80px',
                    height: '80px',
                    '&:hover': {
                      backgroundColor: 'var(--color-background)',
                    },
                    '&.Mui-expanded': {
                      backgroundColor: 'var(--color-background)',
                      minHeight: '80px',
                    },
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                  },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: 'var(--color-text-primary)',
                      fontSize: { xs: '1rem', md: '1.1rem' }
                    }}
                  >
                    {policy.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: { xs: 1, sm: 2, md: 3 },
                    backgroundColor: 'var(--color-background)'
                  }}
                >
                  {policy.type === 'iframe' ? (
                    <iframe
                      srcDoc={typeof policy.content === 'string' ? policy.content : ''}
                      style={{
                        width: '100%',
                        borderStyle: 'none',
                        height: '0',
                        overflow: 'hidden',
                      }}
                      title={policy.title}
                      onLoad={(e) => resizeIframe(e.target as HTMLIFrameElement)}
                    />
                  ) : (
                    policy.content
                  )}
                </AccordionDetails>
              </Accordion>
              {index < privacyPolicies.length - 1 && (
                <Divider sx={{ borderColor: 'var(--color-border)' }} />
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, fontSize: { xs: '0.8rem', md: '1rem' }, color: 'var(--color-text-primary)' }}>
            {'Note :- If you wish to delete your personal information from our records, please visit '}
            <Link
              component="button"
              type="button"
              onClick={handlePrivacyWebFormClick}
              sx={{
                color: 'inherit',
                font: 'inherit',
                fontWeight: 500,
                textDecoration: 'underline',
                cursor: 'pointer',
                verticalAlign: 'baseline',
                p: 0,
                m: 0,
                border: 'none',
                background: 'none',
                display: 'inline',
              }}
            >
              Privacy Web Form
            </Link>
          </Typography>
        </Box>
      </Box>
    </Layout>
  );
};

export default PrivacyPoliciesPage;
